// src/WarrantyLogisticsPage.js
import React, { useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function qsValue(name) {
  const p = new URLSearchParams(window.location.search);
  return p.get(name) || "";
}

// Convert YYYY-MM-DD -> DD/MM/YYYY (if empty or invalid, return "")
function isoToDmy(iso) {
  const s = (iso || "").trim();
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function WarrantyLogisticsPage() {
  const wrId = useMemo(() => qsValue("wrId").trim(), []);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [option, setOption] = useState("AMK"); // AMK | SWS
  const [documentNo, setDocumentNo] = useState("");

  // Store as ISO for <input type="date">
  const [postingDate, setPostingDate] = useState(""); // YYYY-MM-DD

  const [useMySignature, setUseMySignature] = useState(true);

  async function submit() {
    if (!wrId) return setError("Missing wrId in URL. Example: /warranty_logistics?wrId=WR-xxxxx");
    if (!documentNo.trim()) return setError("Document No. is required.");
    if (!postingDate.trim()) return setError("Posting Date is required.");
    if (!useMySignature) return setError("Use my profile signature must be checked.");

    const ok = window.confirm("This will submit Logistics fields and send an email to Sales admin.\n\nContinue?");
    if (!ok) return;

    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      await apiFetch(`/warranty/${encodeURIComponent(wrId)}/logistics/submit`, {
        method: "POST",
        body: {
          option,
          documentNo: documentNo.trim(),
          // Send as DD/MM/YYYY for your PDFs / Excel fields
          postingDate: isoToDmy(postingDate),
          useMySignature: true,
        },
      });

      setOkMsg("Submitted. Sales Admin will receive an email when the Logistics PDFs are ready.");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <div style={{ fontSize: 18, fontWeight: 900 }}>Warranty (Logistics)</div>

      <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
        wrId: <span style={mono}>{wrId || "-"}</span>
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
        Please fill in the information below and click Submit.
      </div>

      {error ? <div style={{ marginTop: 12, color: "#b91c1c", fontWeight: 800 }}>{error}</div> : null}
      {okMsg ? <div style={{ marginTop: 12, color: "#166534", fontWeight: 800 }}>{okMsg}</div> : null}

      <div
        style={{
          marginTop: 14,
          border: "1px solid #fed7aa",
          background: "#fff7ed",
          borderRadius: 12,
          padding: 12,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Form for Returned Goods</div>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Return Option</div>

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
            <input type="radio" checked={option === "AMK"} onChange={() => setOption("AMK")} disabled={busy} />
            Return to NTP
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
            <input type="radio" checked={option === "SWS"} onChange={() => setOption("SWS")} disabled={busy} />
            Return to SWS &amp; put back to stock
          </label>
        </div>

        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Document No.</div>
            <input
              value={documentNo}
              disabled={busy}
              onChange={(e) => setDocumentNo(e.target.value)}
              style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Posting Date</div>
            <input
              type="date"
              value={postingDate}
              disabled={busy}
              onChange={(e) => setPostingDate(e.target.value)}
              style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
            <input
              type="checkbox"
              checked={!!useMySignature}
              onChange={(e) => setUseMySignature(e.target.checked)}
              disabled={busy}
            />
            Use my profile signature
          </label>
          <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
            Signature will be taken from your Profile.
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <button
            onClick={submit}
            disabled={busy}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              fontWeight: 900,
              background: "#0f172a",
              color: "white",
              opacity: busy ? 0.7 : 1,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}